/**
 * Data Schemas for RetireProApp Application
 * 
 * These schemas define the structure for Income, Expense, and Asset data
 * that are compatible with both frontend forms and Firestore storage.
 */

// ============================================================================
// INCOME SCHEMA
// ============================================================================

export const INCOME_SCHEMA = {
  // Core identification
  id: {
    type: 'string',
    required: true,
    description: 'Unique identifier for the income source',
    example: 'income_1234567890'
  },
  
  // Basic information
  type: {
    type: 'string',
    required: true,
    enum: ['salary', 'business', 'rental', 'passive', 'investment', 'pension', 'other'],
    description: 'Type of income source',
    example: 'salary'
  },
  
  description: {
    type: 'string',
    required: true,
    maxLength: 200,
    description: 'Human-readable description of the income source',
    example: 'Senior Software Engineer - TechCorp'
  },
  
  // Financial details
  currentAmount: {
    type: 'number',
    required: true,
    min: 0,
    description: 'Current monthly/annual amount in INR',
    example: 85000
  },
  
  frequency: {
    type: 'string',
    required: true,
    enum: ['monthly', 'quarterly', 'annually', 'one-time'],
    description: 'Frequency of income payment',
    example: 'monthly'
  },
  
  // Timeline
  startYear: {
    type: 'number',
    required: true,
    min: 2020,
    max: 2050,
    description: 'Year when this income source starts',
    example: 2024
  },
  
  endYear: {
    type: 'number',
    required: true,
    min: 2020,
    max: 2050,
    description: 'Year when this income source ends (null for ongoing)',
    example: 2045
  },
  
  // Growth and projections
  growthRate: {
    type: 'number',
    required: true,
    min: 0,
    max: 50,
    description: 'Expected annual growth rate in percentage',
    example: 4.5
  },
  
  probability: {
    type: 'number',
    required: true,
    min: 0,
    max: 100,
    description: 'Confidence level for this income stream (0-100%)',
    example: 95
  },
  
  // Status and metadata
  isActive: {
    type: 'boolean',
    required: true,
    description: 'Whether this income source is currently active',
    example: true
  },
  
  notes: {
    type: 'string',
    required: false,
    maxLength: 500,
    description: 'Additional notes about this income source',
    example: 'Primary income source with annual reviews'
  },
  
  // Timestamps
  createdAt: {
    type: 'timestamp',
    required: true,
    description: 'When this income source was created',
    example: '2024-01-15T10:30:00Z'
  },
  
  updatedAt: {
    type: 'timestamp',
    required: true,
    description: 'When this income source was last updated',
    example: '2024-01-15T10:30:00Z'
  }
};

// ============================================================================
// EXPENSE SCHEMA
// ============================================================================

export const EXPENSE_SCHEMA = {
  // Core identification
  id: {
    type: 'string',
    required: true,
    description: 'Unique identifier for the expense',
    example: 'expense_1234567890'
  },
  
  // Basic information
  description: {
    type: 'string',
    required: true,
    maxLength: 200,
    description: 'Human-readable description of the expense',
    example: 'Monthly groceries and household items'
  },
  
  category: {
    type: 'string',
    required: true,
    enum: ['medical', 'travel', 'personal', 'home', 'food', 'transport', 'kids', 'parents', 'insurance', 'entertainment', 'other'],
    description: 'Category of the expense',
    example: 'food'
  },
  
  // Financial details
  amount: {
    type: 'number',
    required: true,
    min: 0,
    description: 'Amount in INR',
    example: 5000
  },
  
  frequency: {
    type: 'string',
    required: true,
    enum: ['monthly', 'quarterly', 'annually', 'one-time'],
    description: 'Frequency of the expense',
    example: 'monthly'
  },
  
  // Timeline
  startYear: {
    type: 'number',
    required: true,
    min: 2020,
    max: 2050,
    description: 'Year when this expense starts',
    example: 2024
  },
  
  endYear: {
    type: 'number',
    required: true,
    min: 2020,
    max: 2050,
    description: 'Year when this expense ends',
    example: 2045
  },
  
  // Inflation and projections
  inflationRate: {
    type: 'number',
    required: true,
    min: 0,
    max: 20,
    description: 'Expected annual inflation rate for this expense in percentage',
    example: 3.5
  },
  
  probability: {
    type: 'number',
    required: true,
    min: 0,
    max: 100,
    description: 'Probability of this expense occurring (0-100%)',
    example: 100
  },
  
  // Special flags
  isOneTime: {
    type: 'boolean',
    required: true,
    description: 'Whether this is a one-time expense',
    example: false
  },
  
  isEssential: {
    type: 'boolean',
    required: false,
    default: true,
    description: 'Whether this is an essential expense that cannot be easily reduced',
    example: true
  },
  
  // Additional metadata
  priority: {
    type: 'string',
    required: false,
    enum: ['low', 'medium', 'high', 'critical'],
    default: 'medium',
    description: 'Priority level of this expense',
    example: 'medium'
  },
  
  notes: {
    type: 'string',
    required: false,
    maxLength: 500,
    description: 'Additional notes about this expense',
    example: 'Includes groceries, household items, and personal care products'
  },
  
  // Timestamps
  createdAt: {
    type: 'timestamp',
    required: true,
    description: 'When this expense was created',
    example: '2024-01-15T10:30:00Z'
  },
  
  updatedAt: {
    type: 'timestamp',
    required: true,
    description: 'When this expense was last updated',
    example: '2024-01-15T10:30:00Z'
  }
};

// ============================================================================
// ASSET SCHEMA
// ============================================================================

export const ASSET_SCHEMA = {
  // Core identification
  id: {
    type: 'string',
    required: true,
    description: 'Unique identifier for the asset',
    example: 'asset_1234567890'
  },
  
  // Basic information
  name: {
    type: 'string',
    required: true,
    maxLength: 200,
    description: 'Name or description of the asset',
    example: 'Apple Inc. Stock Portfolio'
  },
  
  type: {
    type: 'string',
    required: true,
    enum: ['Equity', 'Mutual Funds', 'Real Estate', 'PPF', 'Fixed Deposits', 'Cash', 'Gold', 'Bonds', 'EPF', 'NPS', 'Insurance', 'Other'],
    description: 'Type of asset',
    example: 'Equity'
  },
  
  // Financial details
  currentValue: {
    type: 'number',
    required: true,
    min: 0,
    description: 'Current market value in INR',
    example: 500000
  },
  
  purchaseValue: {
    type: 'number',
    required: false,
    min: 0,
    description: 'Original purchase/investment value in INR',
    example: 450000
  },
  
  // Performance metrics
  expectedReturn: {
    type: 'number',
    required: true,
    min: -50,
    max: 50,
    description: 'Expected annual return in percentage',
    example: 12.5
  },
  
  probability: {
    type: 'number',
    required: true,
    min: 0,
    max: 100,
    description: 'Confidence level in the expected return (0-100%)',
    example: 80
  },
  
  // Risk and liquidity
  riskLevel: {
    type: 'string',
    required: true,
    enum: ['Low', 'Medium', 'High'],
    description: 'Risk level of the asset',
    example: 'Medium'
  },
  
  liquidity: {
    type: 'string',
    required: true,
    enum: ['Liquid', 'Illiquid'],
    description: 'Liquidity level of the asset',
    example: 'Liquid'
  },
  
  // Timeline
  acquisitionDate: {
    type: 'date',
    required: true,
    description: 'Date when the asset was acquired',
    example: '2020-03-15'
  },
  
  saleYear: {
    type: 'number',
    required: false,
    min: 2020,
    max: 2050,
    description: 'Planned year of sale (for real estate, etc.)',
    example: 2035
  },
  
  // Additional metadata
  description: {
    type: 'string',
    required: false,
    maxLength: 500,
    description: 'Additional details about the asset',
    example: 'Diversified portfolio of blue-chip stocks'
  },
  
  isActive: {
    type: 'boolean',
    required: true,
    default: true,
    description: 'Whether this asset is currently active',
    example: true
  },
  
  // Timestamps
  createdAt: {
    type: 'timestamp',
    required: true,
    description: 'When this asset was created',
    example: '2024-01-15T10:30:00Z'
  },
  
  updatedAt: {
    type: 'timestamp',
    required: true,
    description: 'When this asset was last updated',
    example: '2024-01-15T10:30:00Z'
  }
};

// ============================================================================
// DEBT/LOAN SCHEMA
// ============================================================================

export const DEBT_SCHEMA = {
  // Core identification
  id: {
    type: 'string',
    required: true,
    description: 'Unique identifier for the loan/debt',
    example: 'debt_1234567890'
  },
  
  // Basic information
  type: {
    type: 'string',
    required: true,
    enum: ['Home Loans', 'Car/Vehicle Loans', 'Personal Loans', 'Education Loans', 'Credit Cards', 'Business Loans'],
    description: 'Type of loan/debt',
    example: 'Home Loans'
  },
  
  name: {
    type: 'string',
    required: true,
    maxLength: 200,
    description: 'Name or description of the loan',
    example: 'Home Loan - Primary Residence'
  },
  
  bank: {
    type: 'string',
    required: true,
    maxLength: 100,
    description: 'Bank or financial institution',
    example: 'HDFC Bank'
  },
  
  account: {
    type: 'string',
    required: true,
    maxLength: 50,
    description: 'Account number or identifier',
    example: 'A/c XXXX1234'
  },
  
  // Financial details
  outstanding: {
    type: 'number',
    required: true,
    min: 0,
    description: 'Current outstanding balance in INR',
    example: 5000000
  },
  
  monthlyEMI: {
    type: 'number',
    required: true,
    min: 0,
    description: 'Monthly EMI amount in INR',
    example: 45000
  },
  
  interestRate: {
    type: 'number',
    required: true,
    min: 0,
    max: 50,
    description: 'Interest rate per annum in percentage',
    example: 8.5
  },
  
  tenure: {
    type: 'number',
    required: true,
    min: 1,
    max: 50,
    description: 'Loan tenure in years',
    example: 20
  },
  
  // Timeline
  startDate: {
    type: 'date',
    required: true,
    description: 'Loan start date',
    example: '2020-01-01'
  },
  
  payoffDate: {
    type: 'string',
    required: false,
    description: 'Calculated payoff date',
    example: 'Aug 2040'
  },
  
  // Additional details
  prepayment: {
    type: 'number',
    required: false,
    min: 0,
    default: 0,
    description: 'Any prepayments made so far in INR',
    example: 100000
  },
  
  totalInterest: {
    type: 'number',
    required: false,
    min: 0,
    description: 'Total interest to be paid over loan tenure',
    example: 500000
  },
  
  notes: {
    type: 'string',
    required: false,
    maxLength: 500,
    description: 'Additional notes about the loan',
    example: 'Primary residence loan with tax benefits'
  },
  
  // Status
  isActive: {
    type: 'boolean',
    required: true,
    default: true,
    description: 'Whether the loan is currently active',
    example: true
  },
  
  // Timestamps
  createdAt: {
    type: 'timestamp',
    required: true,
    description: 'When the loan record was created',
    example: '2024-01-15T10:30:00Z'
  },
  
  updatedAt: {
    type: 'timestamp',
    required: true,
    description: 'When the loan record was last updated',
    example: '2024-01-15T10:30:00Z'
  }
};

/**
 * Validates debt/loan data against the schema
 * @param {Object} debtData - Debt data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateDebt = (debtData) => {
  const errors = [];
  
  // Required fields
  if (!debtData.id) errors.push('ID is required');
  if (!debtData.type) errors.push('Loan type is required');
  if (!debtData.name) errors.push('Loan name is required');
  if (!debtData.bank) errors.push('Bank name is required');
  if (!debtData.account) errors.push('Account number is required');
  if (typeof debtData.outstanding !== 'number' || debtData.outstanding <= 0) {
    errors.push('Outstanding amount must be a positive number');
  }
  if (typeof debtData.monthlyEMI !== 'number' || debtData.monthlyEMI <= 0) {
    errors.push('Monthly EMI must be a positive number');
  }
  if (typeof debtData.interestRate !== 'number' || debtData.interestRate <= 0) {
    errors.push('Interest rate must be a positive number');
  }
  if (typeof debtData.tenure !== 'number' || debtData.tenure <= 0) {
    errors.push('Tenure must be a positive number');
  }
  if (!debtData.startDate) errors.push('Start date is required');
  
  // Type validation
  const validTypes = ['Home Loans', 'Car/Vehicle Loans', 'Personal Loans', 'Education Loans', 'Credit Cards', 'Business Loans'];
  if (!validTypes.includes(debtData.type)) {
    errors.push('Invalid loan type');
  }
  
  // Range validation
  if (debtData.interestRate > 50) {
    errors.push('Interest rate cannot exceed 50%');
  }
  
  if (debtData.tenure > 50) {
    errors.push('Tenure cannot exceed 50 years');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// ============================================================================
// SETUP SCHEMA
// ============================================================================

export const SETUP_SCHEMA = {
  currentAge: {
    type: 'number',
    required: true,
    min: 18,
    max: 100,
    description: 'Current age of the user',
    example: 35
  },
  
  retirementYear: {
    type: 'number',
    required: true,
    min: 2024,
    max: 2074,
    description: 'Target year for retirement',
    example: 2050
  },
  
  startYear: {
    type: 'number',
    required: true,
    min: 2023,
    max: 2100,
    description: 'Start year for projections',
    example: 2025
  },
  
  projectionHorizon: {
    type: 'number',
    required: true,
    min: 1,
    max: 60,
    description: 'Projection horizon in years',
    example: 30
  },
  
  defaultExpenseInflation: {
    type: 'number',
    required: true,
    min: 0,
    max: 20,
    description: 'Default annual expense inflation percentage',
    example: 5
  },
  
  defaultIncomeGrowth: {
    type: 'number',
    required: true,
    min: 0,
    max: 50,
    description: 'Default annual income growth percentage',
    example: 8
  },
  
  postRetirementPvReturn: {
    type: 'number',
    required: true,
    min: -50,
    max: 50,
    description: 'Post-retirement real return (percentage)',
    example: 3.5
  },
  
  marginOfSafety: {
    type: 'number',
    required: true,
    min: 0.1,
    max: 10,
    description: 'Margin of safety multiplier (x)',
    example: 1.2
  },
  
  yearsToRetirement: {
    type: 'number',
    required: true,
    min: 0,
    max: 50,
    description: 'Calculated years remaining until retirement',
    example: 26
  },
  
  retirementAge: {
    type: 'number',
    required: true,
    min: 18,
    max: 100,
    description: 'Calculated age at retirement',
    example: 61
  },
  
  lastUpdated: {
    type: 'timestamp',
    required: true,
    description: 'Timestamp of last update',
    example: '2024-01-15T10:30:00.000Z'
  }
};

/**
 * Validates setup data against the schema
 * @param {Object} setupData - Setup data to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export const validateSetup = (setupData) => {
  const errors = [];
  
  if (!setupData) {
    errors.push('Setup data is required');
    return { isValid: false, errors };
  }
  
  // Validate current age
  if (typeof setupData.currentAge !== 'number' || isNaN(setupData.currentAge)) {
    errors.push('Current age must be a valid number');
  } else if (setupData.currentAge < 18 || setupData.currentAge > 100) {
    errors.push('Current age must be between 18 and 100');
  }
  
  // Validate retirement year
  if (typeof setupData.retirementYear !== 'number' || isNaN(setupData.retirementYear)) {
    errors.push('Retirement year must be a valid number');
  } else {
    const currentYear = new Date().getFullYear();
    if (setupData.retirementYear < currentYear) {
      errors.push('Retirement year cannot be in the past');
    } else if (setupData.retirementYear > currentYear + 50) {
      errors.push('Retirement year cannot be more than 50 years in the future');
    }
  }

  // Validate start year
  if (typeof setupData.startYear !== 'number' || isNaN(setupData.startYear)) {
    errors.push('Start year must be a valid number');
  } else {
    const currentYear = new Date().getFullYear();
    if (setupData.startYear < currentYear - 1) {
      errors.push(`Start year cannot be before ${currentYear - 1}`);
    } else if (setupData.startYear > currentYear + 50) {
      errors.push('Start year cannot be more than 50 years in the future');
    }
  }

  // Validate projection horizon
  if (typeof setupData.projectionHorizon !== 'number' || isNaN(setupData.projectionHorizon)) {
    errors.push('Projection horizon must be a valid number');
  } else if (setupData.projectionHorizon < 1 || setupData.projectionHorizon > 60) {
    errors.push('Projection horizon must be between 1 and 60 years');
  }

  // Validate default rates and assumptions
  if (typeof setupData.defaultExpenseInflation !== 'number' || isNaN(setupData.defaultExpenseInflation)) {
    errors.push('Default expense inflation must be a valid number');
  } else if (setupData.defaultExpenseInflation < 0 || setupData.defaultExpenseInflation > 20) {
    errors.push('Expense inflation must be between 0% and 20%');
  }

  if (typeof setupData.defaultIncomeGrowth !== 'number' || isNaN(setupData.defaultIncomeGrowth)) {
    errors.push('Default income growth must be a valid number');
  } else if (setupData.defaultIncomeGrowth < 0 || setupData.defaultIncomeGrowth > 50) {
    errors.push('Income growth must be between 0% and 50%');
  }

  if (typeof setupData.postRetirementPvReturn !== 'number' || isNaN(setupData.postRetirementPvReturn)) {
    errors.push('Post-retirement PV return must be a valid number');
  } else if (setupData.postRetirementPvReturn < -50 || setupData.postRetirementPvReturn > 50) {
    errors.push('PV return must be between -50% and 50%');
  }

  if (typeof setupData.marginOfSafety !== 'number' || isNaN(setupData.marginOfSafety)) {
    errors.push('Margin of safety must be a valid number');
  } else if (setupData.marginOfSafety <= 0 || setupData.marginOfSafety > 10) {
    errors.push('Margin of safety must be greater than 0 and up to 10x');
  }
  
  // Validate calculated fields
  if (typeof setupData.yearsToRetirement !== 'number' || isNaN(setupData.yearsToRetirement)) {
    errors.push('Years to retirement must be a valid number');
  } else if (setupData.yearsToRetirement < 0 || setupData.yearsToRetirement > 50) {
    errors.push('Years to retirement must be between 0 and 50');
  }
  
  if (typeof setupData.retirementAge !== 'number' || isNaN(setupData.retirementAge)) {
    errors.push('Retirement age must be a valid number');
  } else if (setupData.retirementAge < 18 || setupData.retirementAge > 100) {
    errors.push('Retirement age must be between 18 and 100');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// ============================================================================
// USER DATA SCHEMA (Complete user data structure)
// ============================================================================

export const USER_DATA_SCHEMA = {
  uid: {
    type: 'string',
    required: true,
    description: 'Firebase user ID',
    example: 'user_1234567890'
  },
  
  email: {
    type: 'string',
    required: true,
    description: 'User email address',
    example: 'user@example.com'
  },
  
  displayName: {
    type: 'string',
    required: false,
    description: 'User display name',
    example: 'John Doe'
  },
  
  // Financial data collections
  income: {
    type: 'array',
    required: false,
    default: [],
    description: 'Array of income sources',
    items: INCOME_SCHEMA
  },
  
  expenses: {
    type: 'array',
    required: false,
    default: [],
    description: 'Array of expenses',
    items: EXPENSE_SCHEMA
  },
  
  portfolio: {
    type: 'array',
    required: false,
    default: [],
    description: 'Array of assets',
    items: ASSET_SCHEMA
  },
  
  debts: {
    type: 'array',
    required: false,
    default: [],
    description: 'Array of loans and debts',
    items: DEBT_SCHEMA
  },
  
  setup: {
    type: 'object',
    required: false,
    description: 'User retirement setup configuration',
    items: SETUP_SCHEMA
  },
  
  goals: {
    type: 'array',
    required: false,
    default: [],
    description: 'Array of financial goals',
    items: {
      id: { type: 'string', required: true },
      name: { type: 'string', required: true },
      targetAmount: { type: 'number', required: true },
      targetYear: { type: 'number', required: true },
      priority: { type: 'string', enum: ['low', 'medium', 'high'], default: 'medium' },
      description: { type: 'string', required: false },
      createdAt: { type: 'timestamp', required: true },
      updatedAt: { type: 'timestamp', required: true }
    }
  },
  
  // User preferences and settings
  preferences: {
    type: 'object',
    required: false,
    default: {},
    properties: {
      currency: { type: 'string', default: 'INR' },
      inflationRate: { type: 'number', default: 3.5 },
      retirementAge: { type: 'number', default: 60 },
      lifeExpectancy: { type: 'number', default: 85 },
      riskTolerance: { type: 'string', enum: ['conservative', 'moderate', 'aggressive'], default: 'moderate' }
    }
  },
  
  // Timestamps
  createdAt: {
    type: 'timestamp',
    required: true,
    description: 'When user account was created',
    example: '2024-01-15T10:30:00Z'
  },
  
  updatedAt: {
    type: 'timestamp',
    required: true,
    description: 'When user data was last updated',
    example: '2024-01-15T10:30:00Z'
  }
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates income data against the schema
 * @param {Object} incomeData - Income data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateIncome = (incomeData) => {
  const errors = [];
  
  // Required fields
  if (!incomeData.id) errors.push('ID is required');
  if (!incomeData.type) errors.push('Type is required');
  if (!incomeData.description) errors.push('Description is required');
  if (typeof incomeData.currentAmount !== 'number' || incomeData.currentAmount <= 0) {
    errors.push('Current amount must be a positive number');
  }
  if (!incomeData.frequency) errors.push('Frequency is required');
  if (!incomeData.startYear) errors.push('Start year is required');
  if (!incomeData.endYear) errors.push('End year is required');
  
  // Type validation
  const validTypes = ['salary', 'business', 'rental', 'passive', 'investment', 'pension', 'other'];
  if (!validTypes.includes(incomeData.type)) {
    errors.push('Invalid income type');
  }
  
  const validFrequencies = ['monthly', 'quarterly', 'annually', 'one-time'];
  if (!validFrequencies.includes(incomeData.frequency)) {
    errors.push('Invalid frequency');
  }
  
  // Range validation
  if (incomeData.growthRate < 0 || incomeData.growthRate > 50) {
    errors.push('Growth rate must be between 0% and 50%');
  }
  
  if (incomeData.probability < 0 || incomeData.probability > 100) {
    errors.push('Probability must be between 0% and 100%');
  }
  
  if (incomeData.startYear > incomeData.endYear) {
    errors.push('Start year must be before end year');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates expense data against the schema
 * @param {Object} expenseData - Expense data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateExpense = (expenseData) => {
  const errors = [];
  
  // Required fields
  if (!expenseData.id) errors.push('ID is required');
  if (!expenseData.description) errors.push('Description is required');
  if (typeof expenseData.amount !== 'number' || expenseData.amount <= 0) {
    errors.push('Amount must be a positive number');
  }
  if (!expenseData.category) errors.push('Category is required');
  if (!expenseData.frequency) errors.push('Frequency is required');
  if (!expenseData.startYear) errors.push('Start year is required');
  if (!expenseData.endYear) errors.push('End year is required');
  
  // Type validation
  const validCategories = ['medical', 'travel', 'personal', 'home', 'food', 'transport', 'kids', 'parents', 'insurance', 'entertainment', 'other'];
  if (!validCategories.includes(expenseData.category)) {
    errors.push('Invalid expense category');
  }
  
  const validFrequencies = ['monthly', 'quarterly', 'annually', 'one-time'];
  if (!validFrequencies.includes(expenseData.frequency)) {
    errors.push('Invalid frequency');
  }
  
  // Range validation
  if (expenseData.inflationRate < 0 || expenseData.inflationRate > 20) {
    errors.push('Inflation rate must be between 0% and 20%');
  }
  
  if (expenseData.probability < 0 || expenseData.probability > 100) {
    errors.push('Probability must be between 0% and 100%');
  }
  
  if (expenseData.startYear > expenseData.endYear) {
    errors.push('Start year must be before end year');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates asset data against the schema
 * @param {Object} assetData - Asset data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateAsset = (assetData) => {
  const errors = [];
  
  // Required fields
  if (!assetData.id) errors.push('ID is required');
  if (!assetData.name) errors.push('Name is required');
  if (!assetData.type) errors.push('Type is required');
  if (typeof assetData.currentValue !== 'number' || assetData.currentValue <= 0) {
    errors.push('Current value must be a positive number');
  }
  if (typeof assetData.expectedReturn !== 'number' || isNaN(assetData.expectedReturn)) {
    errors.push('Expected return is required');
  }
  if (!assetData.acquisitionDate) errors.push('Acquisition date is required');
  if (!assetData.riskLevel) errors.push('Risk level is required');
  if (!assetData.liquidity) errors.push('Liquidity is required');
  
  // Type validation
  const validTypes = ['Equity', 'Mutual Funds', 'Real Estate', 'PPF', 'Fixed Deposits', 'Cash', 'Gold', 'Bonds', 'EPF', 'NPS', 'Insurance', 'Other'];
  if (!validTypes.includes(assetData.type)) {
    errors.push('Invalid asset type');
  }
  
  const validRiskLevels = ['Low', 'Medium', 'High'];
  if (!validRiskLevels.includes(assetData.riskLevel)) {
    errors.push('Invalid risk level');
  }
  
  const validLiquidity = ['Liquid', 'Illiquid'];
  if (!validLiquidity.includes(assetData.liquidity)) {
    errors.push('Invalid liquidity level');
  }
  
  // Range validation
  if (assetData.expectedReturn < -50 || assetData.expectedReturn > 50) {
    errors.push('Expected return must be between -50% and 50%');
  }
  
  if (assetData.probability < 0 || assetData.probability > 100) {
    errors.push('Probability must be between 0% and 100%');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generates a unique ID for new records
 * @returns {string} - Unique ID
 */
export const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Adds timestamps to a data object
 * @param {Object} data - Data object to add timestamps to
 * @param {boolean} isNew - Whether this is a new record
 * @returns {Object} - Data object with timestamps
 */
export const addTimestamps = (data, isNew = true) => {
  const now = new Date().toISOString();
  return {
    ...data,
    createdAt: isNew ? now : data.createdAt,
    updatedAt: now
  };
};

/**
 * Sanitizes data for Firestore storage
 * @param {Object} data - Data to sanitize
 * @returns {Object} - Sanitized data
 */
export const sanitizeForFirestore = (data) => {
  // Remove undefined values and convert to plain object
  const sanitized = JSON.parse(JSON.stringify(data));
  
  // Convert dates to Firestore timestamps if needed
  if (sanitized.createdAt && typeof sanitized.createdAt === 'string') {
    sanitized.createdAt = new Date(sanitized.createdAt);
  }
  if (sanitized.updatedAt && typeof sanitized.updatedAt === 'string') {
    sanitized.updatedAt = new Date(sanitized.updatedAt);
  }
  
  return sanitized;
};

/**
 * Converts Firestore data to frontend format
 * @param {Object} data - Data from Firestore
 * @returns {Object} - Data converted for frontend use
 */
export const convertFromFirestore = (data) => {
  // Convert to plain object
  const converted = JSON.parse(JSON.stringify(data));
  
  // Helper function to safely convert timestamp to ISO string
  const safeConvertTimestamp = (timestamp) => {
    try {
      if (timestamp && timestamp.toDate) {
        // Firestore timestamp
        return timestamp.toDate().toISOString();
      } else if (timestamp && typeof timestamp === 'object') {
        // Date object or timestamp object
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          // Invalid date, return current date
          return new Date().toISOString();
        }
        return date.toISOString();
      } else if (timestamp && typeof timestamp === 'string') {
        // String timestamp
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          // Invalid date string, return current date
          return new Date().toISOString();
        }
        return date.toISOString();
      }
      // If no timestamp or invalid, return current date
      return new Date().toISOString();
    } catch (error) {
      console.warn('Error converting timestamp:', error);
      return new Date().toISOString();
    }
  };
  
  // Convert Firestore timestamps to ISO strings
  if (converted.createdAt) {
    converted.createdAt = safeConvertTimestamp(converted.createdAt);
  }
  
  if (converted.updatedAt) {
    converted.updatedAt = safeConvertTimestamp(converted.updatedAt);
  }
  
  // Handle nested objects (for user data)
  if (converted.income && Array.isArray(converted.income)) {
    converted.income = converted.income.map(item => convertFromFirestore(item));
  }
  
  if (converted.expenses && Array.isArray(converted.expenses)) {
    converted.expenses = converted.expenses.map(item => convertFromFirestore(item));
  }
  
  if (converted.portfolio && Array.isArray(converted.portfolio)) {
    converted.portfolio = converted.portfolio.map(item => convertFromFirestore(item));
  }
  
  if (converted.debts && Array.isArray(converted.debts)) {
    converted.debts = converted.debts.map(item => convertFromFirestore(item));
  }
  
  // Handle setup object
  if (converted.setup && typeof converted.setup === 'object') {
    if (converted.setup.lastUpdated) {
      converted.setup.lastUpdated = safeConvertTimestamp(converted.setup.lastUpdated);
    }
  }
  
  return converted;
};

export default {
  INCOME_SCHEMA,
  EXPENSE_SCHEMA,
  ASSET_SCHEMA,
  DEBT_SCHEMA,
  SETUP_SCHEMA,
  USER_DATA_SCHEMA,
  validateIncome,
  validateExpense,
  validateAsset,
  validateDebt,
  validateSetup,
  generateId,
  addTimestamps,
  sanitizeForFirestore,
  convertFromFirestore
}; 